import React, { useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Mask, Stop, } from 'react-native-svg';
import Animated, { Easing, interpolate, interpolateColor, useAnimatedProps, useAnimatedStyle, useSharedValue, withDelay, withTiming, } from 'react-native-reanimated';
import { AnimatedCircle, AnimatedPath, AnimatedTextInput, } from 'design-system/AnimatedComponents';
import { rem } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import Text, { lineHeight, styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useDefaultScoreBands from '../hooks/useDefaultScoreBands';
import CreditScoreBandsTooltip from './CreditScoreBandsTooltip';
const DEFAULT_DIAMETER = 250;
const LABEL_CONTAINER = rem(40);
const MAX_ANIMATION_DURATION = 5000;
const MIN_ANIMATION_DURATION = 1600;
const EXTRA_PADDING = rem(1);
const getAnimationDuration = (from, target) => Math.max(MAX_ANIMATION_DURATION * (target - from), MIN_ANIMATION_DURATION);
const computeDimensions = (diameter, scoreBands) => {
    const strokeWidth = diameter * 0.06;
    const radius = (diameter - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const halfCircumference = circumference / 2;
    const arrowSize = diameter * 0.06;
    const needleOffset = diameter * 0.02;
    const needleHeight = radius - strokeWidth / 2 - needleOffset;
    const dashGap = diameter * 0.1;
    const segmentLength = diameter * 0.2;
    const counterTextStyle = diameter >= rem(250) ? textStyles.Numbers_48 : textStyles.Numbers_36;
    const counterHeight = counterTextStyle.lineHeight ?? rem(54);
    const chordCount = scoreBands.length;
    const middleSegmentCount = Math.max(chordCount - 2, 0);
    const hasEndSegments = chordCount >= 2;
    const endSegmentLength = hasEndSegments
        ? (halfCircumference -
            (segmentLength * middleSegmentCount + dashGap * (chordCount - 1))) /
            2
        : 0;
    const scoreBandAngles = {};
    let cumulativeArcLength = 0;
    for (let i = 0; i < chordCount; i++) {
        const isEndSegment = hasEndSegments && (i === 0 || i === chordCount - 1);
        const chordLength = isEndSegment ? endSegmentLength : segmentLength;
        const startAngle = (cumulativeArcLength / circumference) * 360;
        cumulativeArcLength += chordLength;
        const endAngle = (cumulativeArcLength / circumference) * 360;
        scoreBandAngles[scoreBands[i].label] = {
            angle: { min: startAngle, max: endAngle },
        };
        if (i < chordCount - 1) {
            cumulativeArcLength += dashGap;
        }
    }
    return {
        diameter,
        strokeWidth,
        radius,
        circumference,
        halfCircumference,
        arrowSize,
        needleHeight,
        dashGap,
        segmentLength,
        chordCount,
        counterTextStyle,
        counterHeight,
        scoreBandAngles,
    };
};
const getDashPattern = (circumference, segmentLength, dashGap, chordCount) => {
    const middleSegmentCount = Math.max(chordCount - 2, 0);
    const middleGapCount = Math.max(middleSegmentCount - 1, 0);
    const remainingSpace = circumference -
        (segmentLength * middleSegmentCount + dashGap * middleGapCount);
    const endSegmentLength = (remainingSpace - 2 * dashGap) / 2;
    const midArray = Array.from({ length: Math.max(middleSegmentCount * 2 - 1, 0) }, (_, i) => (i % 2 === 0 ? segmentLength : dashGap));
    return [endSegmentLength, dashGap, ...midArray, dashGap, endSegmentLength];
};
const findBandForScore = (score, scoreBands) => scoreBands.find((b) => score >= b.min && score <= b.max);
const clampScoreToBands = (score, scoreBands) => {
    if (scoreBands.length === 0)
        return score;
    const { min } = scoreBands[0];
    const { max } = scoreBands[scoreBands.length - 1];
    return Math.min(Math.max(score, min), max);
};
const getScoreBandProgress = (score, scoreBands, scoreBandAngles) => {
    const clampedScore = clampScoreToBands(score, scoreBands);
    const band = findBandForScore(clampedScore, scoreBands);
    const bandAngle = band && scoreBandAngles[band.label]?.angle;
    if (!band || !bandAngle)
        return 0;
    const bandScoreRange = Math.max(band.max - band.min, 1);
    const scorePercentage = (clampedScore - band.min) / bandScoreRange;
    const targetAngle = bandAngle.min + scorePercentage * (bandAngle.max - bandAngle.min);
    return targetAngle / 180;
};
const EMPTY_GRADIENT_STOPS = [];
const CreditScoreGauge = ({ progress, dimensions, score, scoreBands, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { diameter, strokeWidth, radius, circumference, halfCircumference, arrowSize, needleHeight, dashGap, segmentLength, chordCount, } = dimensions;
    const clampedScore = clampScoreToBands(score, scoreBands);
    const currentBand = findBandForScore(clampedScore, scoreBands);
    const gradientStops = currentBand?.gradientStops ??
        scoreBands[0]?.gradientStops ??
        EMPTY_GRADIENT_STOPS;
    const computedGradient = (<LinearGradient id="arcGradient" x1="0" y1="0" x2="1" y2="0">
      {gradientStops.map(([offset, color], index) => (<Stop key={index} offset={offset} stopColor={color}/>))}
    </LinearGradient>);
    const animatedProps = useAnimatedProps(() => ({
        strokeDashoffset: halfCircumference * (1 - progress.value),
    }));
    const needleStyle = useAnimatedStyle(() => {
        const angle = progress.value * 180 - 90;
        return {
            transform: [{ rotate: `${angle}deg` }, { scaleY: -1 }],
        };
    });
    const [stopOffsets, stopColors] = useMemo(() => [gradientStops.map(([o]) => o), gradientStops.map(([, c]) => c)], [gradientStops]);
    const needleFillProps = useAnimatedProps(() => ({
        fill: interpolateColor(1 - progress.value, stopOffsets, stopColors),
    }));
    const center = diameter / 2;
    return (<Svg width={diameter} height={(diameter + strokeWidth) / 2 + EXTRA_PADDING}>
      <Defs>{computedGradient}</Defs>
      <Defs>
        <Mask id="arcMask" x={-strokeWidth} y={-strokeWidth} width={diameter + strokeWidth * 2} height={(diameter + strokeWidth) / 2 + strokeWidth}>
          <Circle cx={center} cy={center} r={radius} stroke="white" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeDasharray={getDashPattern(circumference, segmentLength, dashGap, chordCount).toString()} transform={`rotate(270, ${center}, ${center})`}/>
        </Mask>
      </Defs>
      <Circle cx={center} cy={center} r={radius} stroke={colors.background.dark} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeDasharray={`${halfCircumference}, ${circumference}`} transform={`rotate(180, ${center}, ${center})`} mask="url(#arcMask)"/>
      <AnimatedCircle cx={center} cy={center} r={radius} strokeWidth={strokeWidth} fill="none" strokeDasharray={`${halfCircumference}, ${circumference}`} strokeLinecap="round" animatedProps={animatedProps} transform={`rotate(180, ${center}, ${center})`} mask="url(#arcMask)" stroke="url(#arcGradient)"/>
      <Animated.View style={[styles.needle, { left: center, top: center }, needleStyle]}>
        <Svg width={arrowSize} height={arrowSize} viewBox="0 0 10 10" style={{
            transform: [
                { translateY: needleHeight - arrowSize },
                { translateX: -arrowSize / 2 },
                { rotate: '90deg' },
            ],
        }}>
          <AnimatedPath d="M 9.192654,5.9425 C 9.145564,5.99083 8.967784,6.1975 8.802164,6.3675 7.831234,7.43667 5.298354,9.18667 3.972654,9.72083 3.771314,9.80667 3.262304,9.98833 2.990344,10 2.729754,10 2.481334,9.94 2.244284,9.81833 1.948784,9.64833 1.711734,9.38083 1.581844,9.065 1.498223,8.84583 1.368331,8.19 1.368331,8.17833 1.23844,7.46083 1.167,6.295 1.167,5.00667 1.167,3.77917 1.23844,2.66083 1.344788,1.9325 1.356966,1.92083 1.486857,1.10583 1.628924,0.826667 1.889524,0.316667 2.398534,-5.38319e-8 2.943264,-7.76429e-8 l 0.04708,-2.0582e-9 C 3.345114,0.0124997 4.091174,0.329167 4.091174,0.340834 5.345434,0.875833 7.819864,2.54 8.814344,3.64583 c 0,0 0.28008,0.28417 0.40185,0.46167 0.18997,0.255 0.28414,0.57083 0.28414,0.88667 0,0.3525 -0.10635,0.68083 -0.30768,0.94833 z" animatedProps={needleFillProps}/>
        </Svg>
      </Animated.View>
    </Svg>);
};
const CreditScoreCounter = ({ progress, score, fromScore, minScore, maxScore, textStyle, withoutAnimation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const animatedTextProps = useAnimatedProps(() => ({
        text: `${Math.round(interpolate(progress.value, [0, 1], [minScore, maxScore]))}`,
    }));
    const initialValueRef = useRef(`${withoutAnimation ? score : fromScore ?? minScore}`);
    return (<View style={scrambled
            ? [styles.counterScrambledContainer, { height: textStyle.lineHeight }]
            : null}>
      <BlurAmountView enabled={scrambled} containerStyle={scrambled
            ? [styles.counterBlurContainer, { height: textStyle.fontSize }]
            : null}>
        <AnimatedTextInput editable={false} defaultValue={initialValueRef.current} style={[textStyle, styles.counter, { color: colors.text.primary }]} animatedProps={animatedTextProps} underlineColorAndroid="transparent"/>
      </BlurAmountView>
    </View>);
};
const CreditScoreRangeLabels = ({ score, fromScore, minScore, maxScore, progress, dimensions, scoreBands, withoutTooltip, withoutAnimation, }) => {
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const { diameter, strokeWidth, counterTextStyle, counterHeight } = dimensions;
    const labelOpacity = useSharedValue(withoutAnimation ? 1 : 0);
    useEffect(() => {
        if (withoutAnimation)
            return;
        labelOpacity.value = withDelay(800, withTiming(1, { duration: 400 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const labelStyle = useAnimatedStyle(() => ({
        opacity: labelOpacity.value,
    }));
    const labelText = findBandForScore(score, scoreBands)?.label;
    const labelOffset = labelText ? rem(lineHeight.Text_14) : 0;
    return (<View style={[styles.rangeLabels, { width: diameter }]}>
      <Text NumbersThin-14 Secondary centered style={{
            transform: [{ translateX: (-LABEL_CONTAINER + strokeWidth) / 2 }],
            width: LABEL_CONTAINER,
        }}>
        {minScore}
      </Text>
      <View style={{
            transform: [{ translateY: -counterHeight / 2.2 - labelOffset }],
            marginBottom: -counterHeight / 2 - labelOffset,
        }}>
        {!!labelText && (<Animated.View style={[labelStyle, styles.labelBandContainer]}>
            <BlurAmountView enabled={scrambled}>
              <Text Text-14 Secondary centered>
                {labelText}
              </Text>
            </BlurAmountView>
            {!withoutTooltip && <CreditScoreBandsTooltip />}
          </Animated.View>)}
        <CreditScoreCounter progress={progress} score={score} fromScore={fromScore} minScore={minScore} maxScore={maxScore} textStyle={counterTextStyle} withoutAnimation={withoutAnimation}/>
      </View>
      <Text NumbersThin-14 Secondary centered style={{
            transform: [{ translateX: (LABEL_CONTAINER - strokeWidth) / 2 }],
            width: LABEL_CONTAINER,
        }}>
        {maxScore}
      </Text>
    </View>);
};
const CreditScoreChart = ({ score, fromScore, minScore: minScoreProp, maxScore: maxScoreProp, diameter = DEFAULT_DIAMETER, scoreBands: scoreBandsProp, chartScale = 'linear', withoutTooltip, withoutLabels, withoutAnimation, noVerticalPadding, animationDuration, }) => {
    const styles = useStyles(styleSet);
    const defaultScoreBands = useDefaultScoreBands();
    const scoreBands = scoreBandsProp ?? defaultScoreBands;
    const minScore = minScoreProp ?? scoreBands[0]?.min ?? 0;
    const maxScore = maxScoreProp ?? scoreBands[scoreBands.length - 1]?.max ?? 0;
    const dimensions = useMemo(() => computeDimensions(rem(diameter), scoreBands), [diameter, scoreBands]);
    const isNonLinear = chartScale === 'nonLinear';
    const range = Math.max(maxScore - minScore, 1);
    const toLinearProgress = (value) => Math.min(Math.max((value - minScore) / range, 0), 1);
    const toBandProgress = (value) => getScoreBandProgress(value, scoreBands, dimensions.scoreBandAngles);
    const targetProgress = toLinearProgress(score);
    const fromProgress = fromScore !== undefined ? toLinearProgress(fromScore) : 0;
    const targetBandProgress = isNonLinear ? toBandProgress(score) : 0;
    const fromBandProgress = isNonLinear && fromScore !== undefined ? toBandProgress(fromScore) : 0;
    const computedDuration = isNonLinear
        ? getAnimationDuration(fromBandProgress, targetBandProgress)
        : getAnimationDuration(fromProgress, targetProgress);
    const progress = useSharedValue(withoutAnimation ? targetProgress : fromProgress);
    const bandProgress = useSharedValue(withoutAnimation ? targetBandProgress : fromBandProgress);
    useEffect(() => {
        if (withoutAnimation) {
            progress.value = targetProgress;
            if (isNonLinear)
                bandProgress.value = targetBandProgress;
            return;
        }
        const timing = (target) => withDelay(300, withTiming(target, {
            duration: animationDuration ?? computedDuration,
            easing: Easing.out(Easing.exp),
        }));
        progress.value = timing(targetProgress);
        if (isNonLinear)
            bandProgress.value = timing(targetBandProgress);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [score, maxScore, chartScale, scoreBands]);
    return (<View style={[styles.container, !noVerticalPadding && styles.containerPadding]}>
      <CreditScoreGauge progress={isNonLinear ? bandProgress : progress} dimensions={dimensions} score={score} scoreBands={scoreBands}/>
      {!withoutLabels && (<CreditScoreRangeLabels minScore={minScore} maxScore={maxScore} score={score} fromScore={fromScore} progress={progress} dimensions={dimensions} scoreBands={scoreBands} withoutTooltip={withoutTooltip} withoutAnimation={withoutAnimation}/>)}
    </View>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        alignItems: 'center',
    },
    containerPadding: {
        paddingVertical: rem(24),
    },
    needle: {
        position: 'absolute',
        width: 0,
        transformOrigin: 'top center',
    },
    rangeLabels: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: rem(3),
    },
    labelBandContainer: {
        transform: [{ translateY: rem(2) }],
        flexDirection: 'row',
        gap: rem(4),
        alignItems: 'center',
        justifyContent: 'center',
    },
    counter: {
        textAlign: 'center',
        padding: 0,
        maxWidth: rem(120),
    },
    counterScrambledContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    counterBlurContainer: {
        justifyContent: 'center',
    },
}));
export default CreditScoreChart;
